/**
 * AB#1899 — Suffix-generator for the Services form "Copy from another service"
 * flow.
 *
 * Why this exists
 * ───────────────
 * The BE enforces a per-business + per-category + per-branch name-uniqueness
 * rule on Service. When the owner picked a source from the Copy-from
 * dropdown the FE used to pre-fill the name verbatim — guaranteeing a 400 on
 * Save with `{"errors":{"Name":["A service with this name already exists in
 * this category"]}}` and a confusing generic "Couldn't create service. Try
 * again." toast.
 *
 * Founder direction (CR-2026-06-06, Option A): auto-suffix the name with
 * " (copy)" on Copy-from. Matches Fresha + SaaS norm. The owner edits if
 * they want a different name; saves out of the box otherwise.
 *
 * Contract
 * ────────
 * Returns the first non-colliding name from this sequence:
 *
 *   1. `{base} {suffixFirst}`                — e.g. "Hair Coloring (copy)"
 *   2. `{base} {suffixNth(2)}`               — e.g. "Hair Coloring (copy 2)"
 *   3. `{base} {suffixNth(3)}`               — e.g. "Hair Coloring (copy 3)"
 *   …
 *
 * Collision check is case-insensitive and whitespace-trimmed on both sides
 * to match the BE's `LOWER(TRIM(Name))` uniqueness semantics — owners often
 * paste names with trailing spaces and the BE normalises them, so the FE
 * needs to as well or the suggestion would still collide.
 *
 * The caller is responsible for scoping `existing` to the right tuple
 * (business + category + branch). This helper is pure and oblivious to
 * scope.
 *
 * AR variant
 * ──────────
 * The caller passes localized suffix functions. For AR the suffix-first is
 * "(نسخة)" and the suffix-nth(n) is "(نسخة n)" — the noun نسخة does not
 * pluralize for the counter, only the numeric is appended.
 */

/**
 * Build a unique service name by appending a copy-suffix.
 *
 * @param base         The source service's name.
 * @param existing     Names of every service in the same (category, branch)
 *                     scope, including the source itself. Order irrelevant.
 * @param suffixFirst  The first-copy suffix (e.g. "(copy)" / "(نسخة)").
 * @param suffixNth    Builder for the nth-copy suffix where n >= 2
 *                     (e.g. n => `(copy ${n})` / n => `(نسخة ${n})`).
 * @returns            The first non-colliding candidate name.
 */
export function generateUniqueServiceName(
    base: string,
    existing: ReadonlyArray<string>,
    suffixFirst: string,
    suffixNth: (n: number) => string,
): string {
    const trimmedBase = base.trim();
    // Case-insensitive + trimmed lookup set — matches BE LOWER(TRIM(Name)).
    const taken = new Set(
        existing.map(name => name.trim().toLowerCase()),
    );

    const firstCandidate = `${trimmedBase} ${suffixFirst}`;
    if (!taken.has(firstCandidate.toLowerCase())) {
        return firstCandidate;
    }

    // Walk n = 2, 3, 4, … until we find a non-colliding candidate. There's
    // no realistic upper bound where this would loop unbounded — a salon
    // with 1000 copies of one service has a different problem — but cap at
    // a defensive ceiling so the function can never wedge the UI thread.
    for (let n = 2; n <= 1000; n++) {
        const candidate = `${trimmedBase} ${suffixNth(n)}`;
        if (!taken.has(candidate.toLowerCase())) {
            return candidate;
        }
    }

    // Should never hit in practice — fall back to the original suggestion
    // so the owner gets *something* editable rather than an empty field.
    return `${trimmedBase} ${suffixFirst}`;
}
